/**
 * Stateless Chat Hook
 *
 * Complete hook for stateless chat that handles:
 * 1. Run creation via POST
 * 2. SSE streaming for events
 * 3. Run lifecycle management
 *
 * This replaces the WebSocket-based useChatStream for the stateless architecture.
 */

import { completeRun, startRun } from '@platform/summary-ui';
import { addEntity } from '@platform/timeline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { RootState } from '../../../store/store';
import {
  type CreateRunParams,
  type CreateRunResult,
  cancelRun,
  connectToSSE,
  createRun,
  getRunStatus,
  type SEMEvent,
  type SSEConnection,
  type StreamFrameMetadata,
  type TerminalEvent,
  type TerminalEventType,
} from '../api/sseClient';
import { routeSemEvent, type SemEvent, stableIdFromItemId } from '../handlers/semEventHandler';
import { RUN_FAILURE_FALLBACK_MESSAGE } from '../runFailureMessage';
import { clearPendingStreamRun, selectPendingStreamRun } from '../state/chatQueueSlice';

// Debug logging utility - enable with: localStorage.setItem('__SSE_DEBUG__', 'true')
function devDebug(tag: string, data?: Record<string, unknown>): void {
  try {
    if (localStorage.getItem('__SSE_DEBUG__') === 'true') {
      console.log(`[stateless-chat] ${tag}`, data ?? '');
    }
  } catch {
    // ignore localStorage errors
  }
}

export interface UseStatelessChatProps {
  /** Conversation ID (client-generated or from server) */
  convId: string | null;
  /** Profile to use for the chat */
  profile?: string;
  /** Draft bundle ID for prompt resolution */
  draftBundleId?: string;
  /** Whether the hook is enabled */
  enabled?: boolean;
  /** Optional frame-level dedupe gate shared with conversation rehydration */
  shouldProcessFrame?: (params: {
    runId: string;
    seq?: number;
    eventType: 'sem' | TerminalEventType;
  }) => boolean;
}

export interface SendMessageOptions {
  text: string;
  messageEntrypoint: string;
  context?: Record<string, unknown>;
  clientMessageId?: string;
}

export interface UseStatelessChatReturn {
  /** Whether currently connected to SSE stream */
  connected: boolean;
  /** Whether a run is in progress */
  isRunning: boolean;
  /** Current run ID (if any) */
  currentRunId: string | null;
  /** Server-assigned conversation ID */
  serverConvId: string | null;
  /** Last error message */
  error: string | null;
  /** Send a message to start a new run */
  sendMessage: (options: SendMessageOptions) => Promise<CreateRunResult>;
  /** Cancel the current run */
  cancelCurrentRun: () => Promise<void>;
}

/**
 * Complete stateless chat hook
 *
 * Handles run creation and SSE streaming in one hook.
 * Use this instead of useChatStream for the stateless architecture.
 */
export function useStatelessChat({
  convId,
  profile,
  draftBundleId,
  enabled = true,
  shouldProcessFrame,
}: UseStatelessChatProps): UseStatelessChatReturn {
  const submitDebounceMs = 400;
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const connectionRef = useRef<SSEConnection | null>(null);
  const [connected, setConnected] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [serverConvId, setServerConvId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs for values accessed inside SSE callbacks.
  // Using refs prevents stale closures: the SSE connection is long-lived and the
  // callbacks must always read the latest serverConvId/convId without requiring
  // the useEffect to tear down and recreate the connection on every change.
  const serverConvIdRef = useRef<string | null>(null);
  const convIdRef = useRef<string | null>(convId);
  const prevConvIdRef = useRef<string | null>(convId);
  const currentRunSummaryStableIdRef = useRef<string | null>(null);
  const createRunInFlightRef = useRef<{
    promise: Promise<CreateRunResult>;
    forConvId: string | null;
  } | null>(null);
  const lastSubmitAtRef = useRef<number>(0);

  // Cleanup function
  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.abort();
      connectionRef.current = null;
    }
    setConnected(false);
  }, []);

  // Reset conversation-scoped state when switching conversations.
  // Without this, sendMessage may continue targeting the prior serverConvId.
  useEffect(() => {
    convIdRef.current = convId;
    if (prevConvIdRef.current === convId) {
      return;
    }
    prevConvIdRef.current = convId;
    disconnect();
    serverConvIdRef.current = null;
    currentRunSummaryStableIdRef.current = null;
    setServerConvId(null);
    setCurrentRunId(null);
    setIsRunning(false);
    setError(null);
  }, [convId, disconnect]);

  // Event handlers stored in refs so the SSE useEffect dep array is stable.
  // The handlers always read the latest convId/serverConvId via refs.
  const handleEventRef = useRef((_semData: SEMEvent, _meta: StreamFrameMetadata) => {});
  const shouldProcessFrameRef = useRef(shouldProcessFrame);
  useEffect(() => {
    shouldProcessFrameRef.current = shouldProcessFrame;
  }, [shouldProcessFrame]);

  const canProcessFrame = useCallback(
    (meta: StreamFrameMetadata, eventType: 'sem' | TerminalEventType) => {
      const gate = shouldProcessFrameRef.current;
      if (!gate) {
        return true;
      }
      return gate({ runId: meta.runId, seq: meta.seq, eventType });
    },
    []
  );

  handleEventRef.current = (semData: SEMEvent, meta: StreamFrameMetadata) => {
    if (!canProcessFrame(meta, 'sem')) {
      return;
    }
    const effectiveConvId = serverConvIdRef.current || convIdRef.current;
    if (!semData.sem || !semData.event || !effectiveConvId) return;

    const ev = semData.event as SemEvent;
    const now = Date.now();

    devDebug('event', { type: ev.type, id: ev.id });

    // Handle run.failed specifically
    if (ev.type === 'run.failed') {
      const runIdFromEvent =
        typeof (ev.data as any)?.run_id === 'string' ? (ev.data as any).run_id : ev.id;
      const userMessage =
        typeof (ev.data as any)?.user_message === 'string'
          ? (ev.data as any).user_message
          : RUN_FAILURE_FALLBACK_MESSAGE;

      if (runIdFromEvent) {
        const stableId = stableIdFromItemId(runIdFromEvent);
        const state = store.getState();
        const activeRun = state.summaryUi?.byConvId?.[effectiveConvId]?.activeRun;

        if (!activeRun || activeRun.stableId !== stableId) {
          dispatch(startRun({ convId: effectiveConvId, stableId, now }));
        }
        dispatch(
          completeRun({
            convId: effectiveConvId,
            stableId,
            success: false,
            error: userMessage,
            now,
          })
        );
      }
    }

    // Route the event through shared handler
    routeSemEvent({ dispatch, convId: effectiveConvId, ev, now });

    const activeRunAfterEvent = store.getState().summaryUi?.byConvId?.[effectiveConvId]?.activeRun;
    if (
      activeRunAfterEvent &&
      (activeRunAfterEvent.status === 'started' ||
        activeRunAfterEvent.status === 'streaming' ||
        activeRunAfterEvent.updatedAt === now)
    ) {
      currentRunSummaryStableIdRef.current = activeRunAfterEvent.stableId;
    }
  };

  const handleTerminalRef = useRef((_event: TerminalEvent, _meta: StreamFrameMetadata) => {});
  handleTerminalRef.current = (event: TerminalEvent, meta: StreamFrameMetadata) => {
    if (!canProcessFrame(meta, event.type)) {
      return;
    }
    devDebug('terminal', { type: event.type });

    const effectiveConvId = serverConvIdRef.current || convIdRef.current;
    const now = Date.now();

    const errorMessage =
      event.type === 'failed' && typeof event.data?.error_message === 'string'
        ? event.data.error_message
        : null;
    if (effectiveConvId) {
      const state = store.getState();
      const activeRun = state.summaryUi?.byConvId?.[effectiveConvId]?.activeRun;

      const fallbackStableID =
        activeRun && (activeRun.status === 'started' || activeRun.status === 'streaming')
          ? activeRun.stableId
          : null;
      const summaryStableID = currentRunSummaryStableIdRef.current ?? fallbackStableID;

      if (!summaryStableID) {
        setConnected(false);
        setIsRunning(false);
        setCurrentRunId(null);
        currentRunSummaryStableIdRef.current = null;

        if (event.type === 'failed') {
          const userFacingError = errorMessage ?? 'Run failed';
          setError(userFacingError);

          // Add error entity to timeline
          dispatch(
            addEntity({
              convId: effectiveConvId,
              entity: {
                id: `error-${Date.now()}`,
                kind: 'message',
                timestamp: Date.now(),
                props: {
                  role: 'system',
                  content: `Error: ${userFacingError}`,
                  severity: 'error',
                },
              } as any,
            })
          );
        }
        return;
      }

      dispatch(
        completeRun({
          convId: effectiveConvId,
          stableId: summaryStableID,
          success: event.type !== 'failed',
          error: errorMessage ?? undefined,
          now,
        })
      );
    }

    setConnected(false);
    setIsRunning(false);
    setCurrentRunId(null);
    currentRunSummaryStableIdRef.current = null;

    if (event.type === 'failed') {
      const userFacingError = errorMessage ?? 'Run failed';
      setError(userFacingError);

      // Add error entity to timeline
      if (effectiveConvId) {
        dispatch(
          addEntity({
            convId: effectiveConvId,
            entity: {
              id: `error-${Date.now()}`,
              kind: 'message',
              timestamp: Date.now(),
              props: {
                role: 'system',
                content: `Error: ${userFacingError}`,
                severity: 'error',
              },
            } as any,
          })
        );
      }
    }
  };

  const handleErrorRef = useRef((_err: Error) => {});
  handleErrorRef.current = (err: Error) => {
    devDebug('error', { message: err.message });
    setError(err.message);
    setConnected(false);
    setIsRunning(false);
  };

  // Connect to SSE when we have a runId.
  // Deps are intentionally limited to currentRunId and enabled so the connection
  // is stable — handlers are accessed via refs and always see latest state.
  useEffect(() => {
    if (!enabled || !currentRunId) {
      disconnect();
      return;
    }

    devDebug('connecting', { runId: currentRunId });

    connectionRef.current = connectToSSE({
      runId: currentRunId,
      onEvent: (e, meta) => handleEventRef.current(e, meta),
      onTerminal: (e, meta) => handleTerminalRef.current(e, meta),
      onError: (e) => handleErrorRef.current(e),
      onConnected: () => {
        devDebug('connected');
        setConnected(true);
        setError(null);
      },
      onDisconnected: () => {
        devDebug('disconnected');
        setConnected(false);
      },
    });

    return () => {
      disconnect();
    };
  }, [currentRunId, enabled, disconnect]);

  // Pick up runs created by the chat queue (widget submissions etc.) and open SSE.
  // The queue thunk cannot open SSE itself because it runs outside React; it stores
  // the result in Redux and we consume it here to trigger the SSE connection effect.
  const effectiveQueueConvId = serverConvId || convId;
  const pendingStreamRun = useSelector(selectPendingStreamRun(effectiveQueueConvId));

  useEffect(() => {
    if (!pendingStreamRun || !enabled) return;

    devDebug('pendingStreamRun', {
      runId: pendingStreamRun.runId,
      serverConvId: pendingStreamRun.serverConvId,
    });

    // Update server conversation ID if the queue's createRun returned one we don't have yet
    if (pendingStreamRun.serverConvId && pendingStreamRun.serverConvId !== serverConvId) {
      serverConvIdRef.current = pendingStreamRun.serverConvId;
      setServerConvId(pendingStreamRun.serverConvId);
    }

    // Trigger SSE connection for the queued run
    setIsRunning(true);
    setCurrentRunId(pendingStreamRun.runId);
    currentRunSummaryStableIdRef.current = null;

    // Clear the pending state so we don't re-consume on next render
    dispatch(clearPendingStreamRun({ convId: effectiveQueueConvId || '' }));
  }, [pendingStreamRun, enabled, serverConvId, effectiveQueueConvId, dispatch]);

  // Send a message (create a run)
  const sendMessage = useCallback(
    async (options: SendMessageOptions): Promise<CreateRunResult> => {
      if (!enabled) {
        throw new Error('Stateless chat is not enabled');
      }

      const effectiveConvId = serverConvId || convId || null;
      if (
        createRunInFlightRef.current &&
        createRunInFlightRef.current.forConvId === effectiveConvId
      ) {
        return createRunInFlightRef.current.promise;
      }

      const now = Date.now();
      if (now - lastSubmitAtRef.current < submitDebounceMs) {
        throw new Error('Generating response...');
      }
      lastSubmitAtRef.current = now;

      if (currentRunId) {
        try {
          const runStatus = await getRunStatus(currentRunId);
          if (runStatus.state === 'pending' || runStatus.state === 'running') {
            throw new Error('Generating response...');
          }
          setIsRunning(false);
          setCurrentRunId(null);
          currentRunSummaryStableIdRef.current = null;
        } catch {
          throw new Error('Generating response...');
        }
      }

      setError(null);
      setIsRunning(true);

      const params: CreateRunParams = {
        conversationId: serverConvId || convId || undefined,
        prompt: options.text,
        profileSlug: profile || undefined,
        messageEntrypoint: options.messageEntrypoint,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        clientTime: new Date().toISOString(),
        clientMessageId: options.clientMessageId,
        draftBundleId,
        context: options.context,
      };

      devDebug('createRun', { params });

      const createPromise = (async (): Promise<CreateRunResult> => {
        try {
          const result = await createRun(params);
          devDebug('runCreated', { runId: result.runId, convId: result.conversationId });

          // Update server conversation ID if this is the first run.
          // Write to ref synchronously so SSE events arriving immediately use the correct convId.
          if (!serverConvId && result.conversationId) {
            serverConvIdRef.current = result.conversationId;
            setServerConvId(result.conversationId);
          }

          // Set current run ID - this triggers SSE connection
          setCurrentRunId(result.runId);
          currentRunSummaryStableIdRef.current = null;

          return result;
        } catch (err) {
          setIsRunning(false);
          const errorMessage = err instanceof Error ? err.message : 'Failed to create run';
          setError(errorMessage);
          throw err;
        }
      })();

      createRunInFlightRef.current = { promise: createPromise, forConvId: effectiveConvId };
      try {
        return await createPromise;
      } finally {
        if (createRunInFlightRef.current?.promise === createPromise) {
          createRunInFlightRef.current = null;
        }
      }
    },
    [enabled, convId, currentRunId, serverConvId, profile, draftBundleId]
  );

  // Cancel the current run
  const cancelCurrentRun = useCallback(async (): Promise<void> => {
    if (!currentRunId) return;

    devDebug('cancelRun', { runId: currentRunId });

    try {
      await cancelRun(currentRunId);
      disconnect();
      setIsRunning(false);
      setCurrentRunId(null);
      currentRunSummaryStableIdRef.current = null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel run';
      setError(errorMessage);
      throw err;
    }
  }, [currentRunId, disconnect]);

  return {
    connected,
    isRunning,
    currentRunId,
    serverConvId,
    error,
    sendMessage,
    cancelCurrentRun,
  };
}
